import { ChangeDetectionStrategy, Component, input } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { InputOutline, TextareaComponent } from "@sxm-content-creators/tools-ds";
import { ImageUploadComponent } from "../../../../components/image-upload/image-upload.component";
import { ThemeAssetUploadResponse, ThemeConfigResponse } from "../../models/theme.model";

/** A full 6-digit hex color (`#RRGGBB`) — the only form a native `<input type="color">` accepts. */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
/** Swatch fallback when the hex field is empty or mid-typing (partial/invalid hex). */
const SWATCH_FALLBACK = "#ffffff";

/**
 * The PWA tab's form (web app manifest branding). Text fields map straight to `themeConfig.pwa*`;
 * the two app-icon fields each pair an asset key (`^public/.+`, sent as `pwaAppIcon*AssetKey`) with
 * its preview URL (read-only, used to re-render in edit mode; never sent), populated by the reusable
 * uploader's `uploaded` event. Colors are stored as the raw hex string the user typed/picked.
 */
export type PwaGroup = FormGroup<{
    pwaName: FormControl<string>;
    pwaNameShort: FormControl<string>;
    pwaDescription: FormControl<string>;
    pwaThemeColor: FormControl<string>;
    pwaBackgroundColor: FormControl<string>;
    pwaAppIcon192AssetKey: FormControl<string>;
    pwaAppIcon192Url: FormControl<string>;
    pwaAppIcon512AssetKey: FormControl<string>;
    pwaAppIcon512Url: FormControl<string>;
}>;

/** Builds the PWA group, optionally pre-filled from a loaded theme config (edit mode). */
export function createPwaGroup(fb: FormBuilder, config?: ThemeConfigResponse | null): PwaGroup {
    return fb.nonNullable.group({
        pwaName: fb.nonNullable.control(config?.pwaName ?? ""),
        pwaNameShort: fb.nonNullable.control(config?.pwaNameShort ?? ""),
        pwaDescription: fb.nonNullable.control(config?.pwaDescription ?? ""),
        pwaThemeColor: fb.nonNullable.control(config?.pwaThemeColor ?? ""),
        pwaBackgroundColor: fb.nonNullable.control(config?.pwaBackgroundColor ?? ""),
        pwaAppIcon192AssetKey: fb.nonNullable.control(config?.pwaAppIcon192AssetKey ?? ""),
        pwaAppIcon192Url: fb.nonNullable.control(config?.pwaAppIcon192Url ?? ""),
        pwaAppIcon512AssetKey: fb.nonNullable.control(config?.pwaAppIcon512AssetKey ?? ""),
        pwaAppIcon512Url: fb.nonNullable.control(config?.pwaAppIcon512Url ?? "")
    });
}

/**
 * "Manifest Branding" section of the theme form (PWA tab). App name/short/description, theme &
 * background colors (a native color swatch + a synced hex text field), and two square app icons
 * uploaded via the reusable uploader. The group is parent-owned: this component only reads/mutates
 * the passed-in PwaGroup.
 */
@Component({
    selector: "app-theme-pwa",
    templateUrl: "./theme-pwa.component.html",
    styleUrl: "./theme-pwa.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, InputOutline, TextareaComponent, ImageUploadComponent]
})
export class ThemePwaComponent {
    /** Parent-owned PWA group; this component only reads/mutates it. */
    readonly pwa = input.required<PwaGroup>();

    /** Value for the native color swatch: the control's hex when valid, else white (empty/partial). */
    swatchColor(value: string): string {
        return HEX_COLOR.test(value) ? value : SWATCH_FALLBACK;
    }

    /** Picking a color from the swatch writes the (uppercased) hex back to the paired hex control. */
    onColorPicked(control: FormControl<string>, event: Event): void {
        control.setValue((event.target as HTMLInputElement).value.toUpperCase());
    }

    /** Stores the uploaded 192px icon asset key + preview URL on the group. */
    onIcon192Uploaded(upload: ThemeAssetUploadResponse): void {
        this.pwa().controls.pwaAppIcon192AssetKey.setValue(upload.key);
        this.pwa().controls.pwaAppIcon192Url.setValue(upload.url);
    }

    /** Stores the uploaded 512px icon asset key + preview URL on the group. */
    onIcon512Uploaded(upload: ThemeAssetUploadResponse): void {
        this.pwa().controls.pwaAppIcon512AssetKey.setValue(upload.key);
        this.pwa().controls.pwaAppIcon512Url.setValue(upload.url);
    }
}
